import { useLayoutEffect, useRef } from "react"
import type { TextareaHTMLAttributes } from "react"

/*
 * A textarea that grows to fit its text instead of scrolling inside a fixed box.
 *
 * The job sentence is written in two places now — the Name-the-job screen and the
 * edit modal — and both need the field to expand line by line as it is typed. The
 * height is reset to `auto` before measuring so the field also shrinks back as text
 * is deleted; the measure runs on every `value` change, which a controlled field
 * emits per keystroke. Pair it with `overflow: hidden` in CSS so `scrollHeight`
 * stays honest and no inner scrollbar appears.
 */

type AutoGrowTextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  value: string
}

export default function AutoGrowTextarea({ value, ...rest }: AutoGrowTextareaProps) {
  const ref = useRef<HTMLTextAreaElement>(null)

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    el.style.height = "auto"
    el.style.height = `${el.scrollHeight}px`
  }, [value])

  return <textarea ref={ref} rows={1} value={value} {...rest} />
}
